import React from 'react';
import { AlertTriangle, CheckCircle, XCircle, RefreshCw, X } from 'lucide-react';
import { GridEditorError, GridEditorErrorType } from '../types';

interface ErrorDisplayProps {
  errors: GridEditorError[];
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
}

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  errors,
  onRetry,
  onDismiss,
  className = ''
}) => {
  if (errors.length === 0) return null;

  const getErrorIcon = (errorType: GridEditorErrorType) => {
    switch (errorType) {
      case GridEditorErrorType.VALIDATION_ERROR:
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case GridEditorErrorType.PERMISSION_ERROR:
        return <XCircle className="h-4 w-4 text-red-600" />;
      case GridEditorErrorType.NETWORK_ERROR:
        return <RefreshCw className="h-4 w-4 text-blue-600" />;
      case GridEditorErrorType.SERVER_ERROR:
        return <XCircle className="h-4 w-4 text-red-600" />;
      case GridEditorErrorType.NOT_FOUND_ERROR:
        return <AlertTriangle className="h-4 w-4 text-orange-600" />;
      case GridEditorErrorType.DUPLICATE_ERROR:
        return <AlertTriangle className="h-4 w-4 text-purple-600" />;
      default:
        return <XCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getErrorColor = (errorType: GridEditorErrorType) => {
    switch (errorType) {
      case GridEditorErrorType.VALIDATION_ERROR:
        return 'border-yellow-200 bg-yellow-50';
      case GridEditorErrorType.PERMISSION_ERROR:
        return 'border-red-200 bg-red-50';
      case GridEditorErrorType.NETWORK_ERROR:
        return 'border-blue-200 bg-blue-50';
      case GridEditorErrorType.SERVER_ERROR:
        return 'border-red-200 bg-red-50';
      case GridEditorErrorType.NOT_FOUND_ERROR:
        return 'border-orange-200 bg-orange-50';
      case GridEditorErrorType.DUPLICATE_ERROR:
        return 'border-purple-200 bg-purple-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  const getErrorTitle = (errorType: GridEditorErrorType) => {
    switch (errorType) {
      case GridEditorErrorType.VALIDATION_ERROR:
        return 'Erreur de validation';
      case GridEditorErrorType.PERMISSION_ERROR:
        return 'Erreur d\'autorisation';
      case GridEditorErrorType.NETWORK_ERROR:
        return 'Erreur réseau';
      case GridEditorErrorType.SERVER_ERROR:
        return 'Erreur serveur';
      case GridEditorErrorType.NOT_FOUND_ERROR:
        return 'Ressource non trouvée';
      case GridEditorErrorType.DUPLICATE_ERROR:
        return 'Compteur en doublon';
      default:
        return 'Erreur inconnue';
    }
  };

  const hasRetryableErrors = errors.some(error => error.retryable);

  return (
    <div className={`space-y-2 ${className}`}>
      {errors.map((error, index) => (
        <div
          key={`${error.type}-${index}`}
          className={`border rounded-lg p-3 ${getErrorColor(error.type)}`}
        >
          <div className="flex items-start space-x-2">
            {getErrorIcon(error.type)}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-900">
                  {getErrorTitle(error.type)}
                </h4>
                {onDismiss && (
                  <button
                    onClick={onDismiss}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <p className="text-sm text-gray-700 mt-1">
                {error.message}
              </p>
              {error.elementId && (
                <p className="text-xs text-gray-500 mt-1">
                  Élément: {error.elementId}
                </p>
              )}
              {error.operation && (
                <p className="text-xs text-gray-500">
                  Opération: {error.operation}
                </p>
              )}
              {error.details && process.env.NODE_ENV === 'development' && (
                <details className="mt-2">
                  <summary className="text-xs text-gray-500 cursor-pointer">
                    Détails techniques
                  </summary>
                  <pre className="text-xs text-gray-600 mt-1 overflow-auto">
                    {JSON.stringify(error.details, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      ))}
      
      {hasRetryableErrors && onRetry && (
        <div className="flex justify-end">
          <button
            onClick={onRetry}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 transition-colors"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Réessayer
          </button>
        </div>
      )}
    </div>
  );
};

// Composant pour afficher un message de succès
interface SuccessMessageProps {
  message: string;
  onDismiss?: () => void;
  className?: string;
}

export const SuccessMessage: React.FC<SuccessMessageProps> = ({
  message,
  onDismiss,
  className = ''
}) => {
  return (
    <div className={`border border-green-200 bg-green-50 rounded-lg p-3 ${className}`}>
      <div className="flex items-start space-x-2">
        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-green-900">
              Sauvegarde réussie
            </h4>
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="text-green-400 hover:text-green-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <p className="text-sm text-green-700 mt-1">
            {message}
          </p>
        </div>
      </div>
    </div>
  );
};

// Composant pour afficher un message d'avertissement (succès partiel)
interface WarningMessageProps {
  message: string;
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
}

export const WarningMessage: React.FC<WarningMessageProps> = ({
  message,
  onRetry,
  onDismiss,
  className = ''
}) => {
  return (
    <div className={`border border-yellow-200 bg-yellow-50 rounded-lg p-3 ${className}`}>
      <div className="flex items-start space-x-2">
        <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-yellow-900">
              Sauvegarde partielle
            </h4>
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="text-yellow-400 hover:text-yellow-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <p className="text-sm text-yellow-700 mt-1">
            {message}
          </p>
          {onRetry && (
            <div className="mt-2">
              <button
                onClick={onRetry}
                className="inline-flex items-center px-2 py-1 text-xs font-medium text-yellow-700 bg-yellow-100 rounded hover:bg-yellow-200 transition-colors"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Réessayer
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
